<template>
  <div class="insights-benchmarks-toggle">
    <section class="toggle-wrapper justify-content-between">
      <div class="toggle-options">
        <button
          :class="['toggle-option', { active: !remoteVsOffice }]"
          :disabled="isLoading"
          @click="selectBenchmarks"
        >
          <span class="toggle-option-content">
            Benchmarks
            <i
              v-tooltip="benchmarksTooltipOptions"
              class="hi hi-info toggle-info-icon"
              :title="benchmarksTooltipText"
            />
          </span>
        </button>
        <button
          :class="['toggle-option', { active: remoteVsOffice }]"
          :disabled="isLoading"
          @click="selectRemoteVsOffice"
        >
          <span class="toggle-option-content">
            Remote vs in-office
            <i
              v-tooltip="remoteVsOfficeTooltipOptions"
              class="hi hi-info toggle-info-icon"
              :title="remoteVsOfficeTooltipText"
            />
          </span>
        </button>
      </div>
    </section>
    <div>
      <template v-if="!remoteVsOffice && showBenchmarksAverages">
        <div
          v-if="isLoading"
          class="toggle-loading"
        ></div>
        <template v-else>
          <span
            v-for="legendItem in filteredBenchmarksAveragesLegend"
            :key="legendItem.key"
            class="legend-label ml-10"
          >
            <i
              :class="getIconClass(legendItem)"
              class="hi shape"
            />
            {{ buildLegendItemCopy(legendItem) }}
          </span>
        </template>
      </template>
      <template v-else-if="remoteVsOffice">
        <div>
          <span class="legend-label ml-10">
            <i class="hi shape insights-benchmarks-averages-color-remote" />
            Remote
          </span>
          <span class="legend-label ml-10">
            <i class="hi shape insights-benchmarks-averages-color-on_site" />
            Office
          </span>
        </div>
      </template>
    </div>
    <div
      v-if="!remoteVsOffice && hasCurrentUser && benchmarksAveragesLegendRaw && 'occupation' in benchmarksAveragesLegendRaw"
      class="job-type-info"
    >
      <template v-if="(currentUserRole === 'owner' || currentUserRole === 'manager') && !hasJobTypeAssigned">
        <i class="hi hi-info-filled"></i>
        <a :href="memberProfileURL">Assign a job type</a>
        <span> to this member to view metrics compared against peers' benchmark.</span>
      </template>
      <template v-if="currentUserRole === 'user' && !hasJobTypeAssigned">
        <i class="hi hi-info-filled mr-5"></i>
        Job type benchmark hidden because this member has no job type assigned.
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { InsightsTracking } from '~/mixins';
import { applyTemplate } from '~/utils/url_template';
import { MEMBER_URL } from '~/app_constants';

export default {
  name: 'InsightsBenchmarksToggle',
  mixins: [InsightsTracking],
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      benchmarksAveragesLegendRaw: (state) => state.benchmarksAveragesLegend,
    }),
    ...mapGetters([
      'benchmarksAveragesLegend',
      'showBenchmarksAverages',
      'remoteVsOffice',
      'activeTab',
      'currentUserRole',
      'currentDashboardUser',
      'organizationId',
    ]),
    benchmarksTooltipText() {
      return 'Benchmarks show the typical range of performance for users in your industry, organization, or job type, so you can compare your selected members against it.';
    },
    remoteVsOfficeTooltipText() {
      return 'Remote vs in-office shows the typical performance range in your organization, based on where people work. Use this to compare your selected members.';
    },
    benchmarksTooltipOptions() {
      return { placement: 'top' };
    },
    remoteVsOfficeTooltipOptions() {
      return { placement: 'top' };
    },
    memberProfileURL() {
      return applyTemplate(`${MEMBER_URL}?tab=employment`, {
        organizationId: this.organizationId,
        memberId: this.currentDashboardUser?.id,
      });
    },
    hasJobTypeAssigned() {
      return this.benchmarksAveragesLegendRaw?.occupation?.name;
    },
    hasCurrentUser() {
      return this.currentDashboardUser?.id;
    },
    filteredBenchmarksAveragesLegend() {
      return this.benchmarksAveragesLegend.filter((legendItem) => {
        return !['remote', 'on_site'].includes(legendItem.key);
      });
    },
  },
  methods: {
    ...mapActions([
      'setShowBenchmarksAverages',
      'setRemoteVsOffice',
      'fetchWidgetsForTab',
    ]),
    selectBenchmarks() {
      if (!this.remoteVsOffice) {return;}
      this.toggleComparison(false, 'Benchmarks', 'Remote vs in-office');
    },
    selectRemoteVsOffice() {
      if (this.remoteVsOffice) {return;}
      this.toggleComparison(true, 'Remote vs in-office', 'Benchmarks');
    },
    toggleComparison(value, newValue, previousValue) {
      this.setRemoteVsOffice(value);
      this.trackAnalytics('Insights comparison change', { new_value: newValue, previous_value: previousValue });
      this.fetchWidgetsForTab(this.activeTab);
    },
    getIconClass(legendItem) {
      return `insights-benchmarks-averages-color-${legendItem.key}`;
    },
    buildLegendItemCopy(legendItem) {
      if (['organization', 'occupation'].includes(legendItem.key)) {
        return `${legendItem.name}'s average`;
      }

      return `${legendItem.name} Industry average`;
    },
  },
};
</script>
